var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { draft, launch } from "../state";
import { api } from "../api/client";
import { startAutosave, stopAutosave } from "../state/autosave";
import "../shell/pas-worklist";
import "../shell/pas-banner";
import "../shell/chart-navigator";
import "../components/chart-viewer";
import "../components/save-indicator";
import "../modes/navigate";
let PasActivityView = class PasActivityView extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.activityId = "";
        this.activity = null;
        this.selectedPatientId = null;
        this.selectedDocId = null;
        // Patients that have been opened at least once this session, so the worklist
        // can show a "Viewed" tick for anyone the student has already looked at
        // (distinct from the currently-open patient, which keeps its In progress
        // highlight). A new Set is assigned (not mutated) so Lit's @state setter fires.
        this.viewedPatientIds = new Set();
        this.submitting = false;
        this.result = null;
        this.submitError = "";
        // Present only when the submit response reports the assessment-auto attempt
        // cap has been hit (same lock payload shape as case-view's submitCase).
        this.lockInfo = null;
        // Pre-submit attempt counter for assessment-auto activities (from the GET payload).
        this.attemptInfo = null;
        this.supportState = "idle";
        this.supportTicket = null;
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    /* min-height (not fixed height) → the page scrolls as the student answers,
       so overflow:hidden for the corner radius is safe here (the host grows). */
    :host { display: flex; flex-direction: column; min-height: 100vh;
            container-type: inline-size; container-name: ehr;
            border-radius: 10px; overflow: hidden;
            background: var(--color-slate-50, #f8fafc); }
    /* Two columns at wide widths: the record (worklist + chart nav + chart) on
       the left, the questions panel on the right, each independently
       scrollable within a bounded height so the student isn't long-scrolling
       past the whole record just to reach the next question. */
    .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
              height: 74vh; min-height: 620px;
              border-bottom: 1px solid var(--color-slate-200); }
    .col { min-height: 0; min-width: 0; overflow: hidden; }
    .col.record { display: grid; grid-template-columns: minmax(180px, 240px) minmax(180px, 260px) 1fr;
                  background: var(--color-white); border-right: 1px solid var(--color-slate-200); }
    .col.questions { overflow-y: auto; background: var(--color-white); padding: 16px; }
    .pane { overflow: auto; }
    .pane.worklist { background: var(--color-white); border-right: 1px solid var(--color-slate-200); }
    .pane.nav { background: var(--color-slate-100); border-right: 1px solid var(--color-slate-200); }
    .pane.chart { background: var(--color-white); padding: 16px; }
    h2.pane-title { margin: 0 0 12px; font-size: 15px; padding: 12px 16px;
                    background: var(--color-slate-900); color: var(--color-white); }
    .tasks-title { margin: 0 0 6px; font-size: 15px; color: var(--color-slate-900); }
    .tasks-hint { margin: 0 0 12px; color: var(--color-slate-500); font-size: 13px; }
    .actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-top: 12px; }
    .attempt-note { font-size: 12px; font-weight: 600; color: var(--color-slate-500, #64748b); }
    .attempt-note.final { color: var(--color-amber-600, #d97706); }
    button.submit { padding: 10px 16px; border-radius: var(--radius-md, 6px);
                    background: var(--color-blue-600, #2563eb); color: white;
                    border: 0; cursor: pointer; font-size: 14px; font-weight: 600; }
    button.submit:hover { background: var(--color-blue-700, #1d4ed8); }
    button.submit:disabled { opacity: 0.6; cursor: default; }
    .result-panel { margin-top: 12px; }
    .result { border-radius: var(--radius-md, 8px); padding: 12px 16px; font-size: 14px; }
    .result.pass { background: #ecfdf5; border: 1px solid #16a34a; color: #065f46; }
    .result.fail { background: #fff7ed; border: 1px solid #f59e0b; color: #7c2d12; }
    .result.error { background: #fef2f2; border: 1px solid #dc2626; color: #7f1d1d; }
    .result-head { display: flex; align-items: center; gap: 8px; font-weight: 700; margin-bottom: 6px; }
    .result-pct { font-size: 18px; }
    .result-note { font-size: 13px; color: inherit; }
    .per-field { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
    .per-field li { display: flex; align-items: baseline; gap: 8px; padding: 8px 10px;
                     background: var(--color-white); border: 1px solid var(--color-slate-200);
                     border-left-width: 4px; border-radius: var(--radius-md, 6px); font-size: 13px; }
    .per-field li.ok { border-left-color: #16a34a; }
    .per-field li.warn { border-left-color: #f59e0b; }
    .per-field li.wrong, .per-field li.missing { border-left-color: #dc2626; }
    .pf-icon { flex: 0 0 auto; }
    .pf-label { flex: 0 0 auto; font-weight: 700; color: var(--color-slate-900); }
    .pf-msg { color: var(--color-slate-600); }
    .rationale { margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--color-slate-200);
                 font-size: 13px; line-height: 1.55; color: var(--color-slate-700); }
    .empty { padding: 24px; color: var(--color-slate-500); font-style: italic; }

    /* locked / contact-support panel — same visual pattern as case-view's
       (see routes/case-view.ts renderLocked/renderSupport), reimplemented here
       rather than imported since CaseView isn't a reusable generic component. */
    .locked { display: flex; flex-direction: column; gap: 14px; padding: 8px 2px; margin-top: 12px; }
    .locked .lhead { display: flex; align-items: center; gap: 10px; }
    .locked .licon { width: 40px; height: 40px; border-radius: 50%; flex: 0 0 auto; display: grid;
                     place-items: center; background: #FFFBEB; color: #D97706; font-size: 20px; }
    .locked h2 { margin: 0; font-size: 17px; color: var(--color-slate-900); }
    .locked p { margin: 0; font-size: 13px; line-height: 1.55; color: var(--color-slate-600); }
    .locked .lcard { background: var(--color-slate-50); border: 1px solid var(--color-slate-200);
                     border-radius: var(--radius-md); padding: 12px 14px; font-size: 12px;
                     color: var(--color-slate-600); }
    button.support { align-self: flex-start; display: inline-flex; align-items: center; gap: 8px;
                     padding: 11px 20px; border-radius: var(--radius-md, 6px);
                     background: var(--color-blue-600, #2563eb); color: white; border: 0;
                     cursor: pointer; font-weight: 700; font-size: 14px; }
    button.support:hover { background: var(--color-blue-700, #1d4ed8); }
    button.support:disabled { opacity: .8; cursor: progress; }
    .support-err { color: var(--color-danger, #dc2626); }
    .support-done { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: var(--radius-md);
                    padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; }
    .support-done .sd-head { display: flex; align-items: center; gap: 8px; font-size: 14px;
                             color: var(--color-slate-900); }
    .support-done .sd-tick { width: 22px; height: 22px; border-radius: 50%; flex: 0 0 auto;
                             display: grid; place-items: center; background: #16A34A; color: #fff;
                             font-size: 13px; font-weight: 700; }
    .support-done p { margin: 0; font-size: 13px; line-height: 1.55; color: var(--color-slate-700); }

    @container ehr (max-width: 899px) {
      .layout { display: block; height: auto; }
      .col { overflow: visible; }
      .col.record { display: block; }
      .col.record .pane { max-height: 460px; }
      .col.questions { overflow: visible; border-left: 0; border-top: 1px solid var(--color-slate-200); }
    }
  `; }
    async connectedCallback() {
        super.connectedCallback();
        try {
            if (!this.activity) {
                this.activity = await api.getPasActivity(this.activityId);
            }
            // Parity with case-view: the GET payload carries the attempt counter /
            // lock state for assessment-auto activities, so returning students see
            // "Attempt N of 3" (or the locked screen) before their first submit.
            const asmt = this.activity?.assessment;
            if (asmt) {
                this.attemptInfo = asmt;
                if (asmt.locked) {
                    this.lockInfo = { attempts_used: asmt.attempts_used, max_attempts: asmt.max_attempts };
                }
            }
            // Auto-select the first patient on load; opening it counts as "viewed".
            const first = (this.activity?.worklist ?? [])[0];
            if (first) {
                this.selectedPatientId = first;
                this.viewedPatientIds = new Set([first]);
            }
            if (!draft.value) {
                // Restore any saved draft so answers survive leaving and re-launching.
                let saved = null;
                try {
                    const r = await api.loadDraft(this.activityId);
                    saved = r?.draft ?? null;
                }
                catch (e) {
                    console.warn("loadDraft failed; starting a fresh draft", e);
                }
                const base = { codes: [], episodeMeta: {}, navigateAnswers: {}, saveStatus: "idle" };
                draft.value = saved && typeof saved === "object"
                    ? { ...base, ...saved, saveStatus: "saved" }
                    : base;
            }
            // Start autosave AFTER hydrating, so restoring a draft doesn't immediately overwrite it.
            startAutosave(this.activityId, (id, d) => api.saveDraft(id, d));
        }
        catch (e) {
            console.error("pas-activity-view load failed:", e);
            if (!draft.value) {
                draft.value = { codes: [], episodeMeta: {}, navigateAnswers: {}, saveStatus: "idle" };
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        stopAutosave();
    }
    patientById(id) {
        if (!id)
            return null;
        return (this.activity?.patients ?? []).find((p) => p.id === id) ?? null;
    }
    worklistItems() {
        const refs = this.activity?.worklist ?? [];
        return refs.map((pid) => {
            const p = this.patientById(pid);
            const fullName = p?.name ? `${p.name.given ?? ""} ${p.name.family ?? ""}`.trim() : pid;
            const status = pid === this.selectedPatientId
                ? "in_progress"
                : this.viewedPatientIds.has(pid) ? "viewed" : "pending";
            return { patientId: pid, name: fullName || pid, status };
        });
    }
    onPatientSelect(e) {
        const pid = e.detail.patientId;
        this.selectedPatientId = pid;
        this.selectedDocId = null; // reset to first doc when switching patients
        if (!this.viewedPatientIds.has(pid)) {
            const next = new Set(this.viewedPatientIds);
            next.add(pid);
            this.viewedPatientIds = next;
        }
    }
    onDocFocus(e) {
        this.selectedDocId = e.detail.docId;
    }
    activityTitle() {
        return this.activity?.title || this.activityId;
    }
    async submit() {
        if (this.submitting)
            return;
        this.submitting = true;
        this.submitError = "";
        try {
            const r = await api.submitPasActivity(this.activityId, draft.value);
            this.result = r;
            if (r.assessment) {
                this.attemptInfo = r.assessment;
            }
            if (r.locked) {
                // 4th un-passed attempt refused — keep this final attempt's (summary-tier)
                // result so it renders above the locked panel, matching case-view.
                this.lockInfo = { attempts_used: r.attempts_used ?? 3, max_attempts: r.max_attempts ?? 3 };
            }
            else {
                this.lockInfo = null;
                this.dispatchEvent(new CustomEvent("submission-result", { detail: r, bubbles: true, composed: true }));
            }
        }
        catch (e) {
            this.submitError = e instanceof Error ? e.message : String(e);
            console.error("pas submit failed:", e);
        }
        finally {
            this.submitting = false;
        }
        await this.updateComplete;
        this.renderRoot.querySelector(".result-panel")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
    /** Create a support ticket asking for this activity to be unlocked — same
     *  server-side Desk-ticket flow as case-view's openSupport. */
    async openSupport() {
        if (this.supportState === "sending" || this.supportState === "sent")
            return;
        const name = (launch.value?.actor?.name ?? "").trim();
        const email = (launch.value?.actor?.mbox ?? "").replace(/^mailto:/, "");
        const activity = this.activityTitle();
        const ref = this.activityId;
        const who = name || email || "a student";
        const subject = `Assessment unlock request: ${activity}${name ? ` (${name})` : ""}`;
        const description = "Hi Support Team,\n\n" +
            "I'm currently locked out of an assessment activity after using all my attempts, " +
            "and I'd like it reset so I can have another go.\n\n" +
            `Activity: ${activity}\n` +
            `Activity reference: ${ref}\n` +
            `Student: ${who}\n` +
            `Email: ${email}\n\n` +
            "Thanks very much.";
        this.supportState = "sending";
        try {
            const r = await api.requestUnlock({ subject, description, name });
            this.supportTicket = r.ticket_number ?? null;
            this.supportState = "sent";
        }
        catch (e) {
            console.error("unlock request failed:", e);
            this.supportState = "error";
        }
    }
    renderTieredResult(r) {
        const level = r.feedback_level ?? "full";
        const pct = Math.round(r.score_pct ?? 0);
        const icon = { ok: "✓", warn: "⚠", wrong: "✗", missing: "○" };
        return html `
      <div class="result ${r.passed ? "pass" : "fail"}">
        <div class="result-head">
          <span class="result-pct">${pct}%</span>
          <span>${r.passed ? "Passed" : "Not yet passed"}</span>
        </div>
        ${level === "score"
            ? html `<p class="result-note">Detailed feedback is shown by your tutor.</p>`
            : html `
              ${r.per_field?.length
                ? html `<ul class="per-field">
                    ${r.per_field.map((f) => html `
                      <li class="${f.status}">
                        <span class="pf-icon">${icon[f.status] ?? "•"}</span>
                        <span class="pf-label">${f.display_label ?? f.field}</span>
                        <span class="pf-msg">${f.message}</span>
                      </li>`)}
                  </ul>`
                : ""}
              ${r.rationale_md
                ? html `<div class="rationale">${unsafeHTML(marked.parse(r.rationale_md, { async: false }))}</div>`
                : ""}
            `}
      </div>
    `;
    }
    renderSupport() {
        if (this.supportState === "sent") {
            return html `
        <div class="support-done">
          <div class="sd-head"><span class="sd-tick">✓</span><strong>Request sent</strong></div>
          <p>Our support team has your request to unlock this activity${this.supportTicket
                ? html ` (reference <strong>#${this.supportTicket}</strong>)`
                : ""}. We'll reset it and email you when you can have another go.</p>
        </div>`;
        }
        if (this.supportState === "error") {
            return html `
        <button class="support" @click=${this.openSupport}>Try again</button>
        <p class="support-err">Sorry, we couldn't send your request just then. Please try
           again in a moment, or email <strong>support@talentmed.edu.au</strong> and mention
           this activity.</p>`;
        }
        const sending = this.supportState === "sending";
        return html `
      <button class="support" ?disabled=${sending} @click=${this.openSupport}>
        ${sending ? "Sending request…" : "Contact support to unlock"}
      </button>
      <p>Click the button and we'll send our support team a request to unlock this activity,
         with your details attached. There's nothing else you need to do, we'll review it and
         get you back on track.</p>`;
    }
    renderLocked() {
        const max = this.lockInfo?.max_attempts ?? 3;
        return html `
      <div class="locked">
        <div class="lhead">
          <div class="licon">!</div>
          <h2>You've used all ${max} attempts</h2>
        </div>
        <p>You've reached the maximum number of attempts for this assessment activity and
           it's now locked. Our support team can review your progress and reset it so you
           can have another go.</p>
        <div class="lcard">Activity: <strong>${this.activityTitle()}</strong></div>
        ${this.renderSupport()}
      </div>`;
    }
    render() {
        if (!this.activity)
            return html `<p class="empty">Loading activity…</p>`;
        const patient = this.patientById(this.selectedPatientId);
        const docs = patient?.documents ?? [];
        const currentDoc = (this.selectedDocId
            ? docs.find((d) => d.id === this.selectedDocId)
            : docs[0]) ?? null;
        return html `
      ${patient ? html `<pas-banner .patient=${patient}></pas-banner>` : ""}
      <div class="layout">
        <div class="col record">
          <div class="pane worklist">
            <h2 class="pane-title">Worklist</h2>
            <pas-worklist
              .items=${this.worklistItems()}
              .selectedId=${this.selectedPatientId}
              @patient-select=${this.onPatientSelect}
            ></pas-worklist>
          </div>
          <div class="pane nav">
            <h2 class="pane-title">${patient?.name ? `${patient.name.given} ${patient.name.family}` : "Patient"}</h2>
            ${docs.length
            ? html `<chart-navigator .documents=${docs} .selectedId=${this.selectedDocId} @doc-focus=${this.onDocFocus}></chart-navigator>`
            : html `<p class="empty">Select a patient from the worklist.</p>`}
          </div>
          <div class="pane chart">
            ${currentDoc
            ? html `<chart-viewer .document=${currentDoc}></chart-viewer>`
            : html `<p class="empty">Select a document.</p>`}
          </div>
        </div>
        <div class="col questions">
          <h2 class="tasks-title">${this.activity.title}</h2>
          <p class="tasks-hint">Answer the questions below, referring to the record on the left.</p>
          ${this.lockInfo
            ? html `<div class="result-panel">
                ${this.result ? this.renderTieredResult(this.result) : ""}
                ${this.renderLocked()}
              </div>`
            : html `
              <mode-navigate .tasks=${this.activity.tasks}></mode-navigate>
              <div class="actions">
                ${this.attemptInfo && !this.attemptInfo.passed
                ? html `<span class="attempt-note ${this.attemptInfo.attempts_used >= this.attemptInfo.max_attempts - 1 ? "final" : ""}">
                      Attempt ${Math.min(this.attemptInfo.attempts_used + 1, this.attemptInfo.max_attempts)} of ${this.attemptInfo.max_attempts}${this.attemptInfo.attempts_used >= this.attemptInfo.max_attempts - 1 ? " (final attempt)" : ""}
                    </span>`
                : html `<save-indicator></save-indicator>`}
                <button class="submit" ?disabled=${this.submitting} @click=${this.submit}>
                  ${this.submitting ? "Submitting…" : "Submit activity"}
                </button>
              </div>
              ${this.submitError
                ? html `<div class="result error">Could not submit: ${this.submitError}. Please try again.</div>`
                : ""}
              ${this.result ? html `<div class="result-panel">${this.renderTieredResult(this.result)}</div>` : ""}
            `}
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], PasActivityView.prototype, "activityId", void 0);
__decorate([
    property({ attribute: false })
], PasActivityView.prototype, "activity", void 0);
__decorate([
    state()
], PasActivityView.prototype, "selectedPatientId", void 0);
__decorate([
    state()
], PasActivityView.prototype, "selectedDocId", void 0);
__decorate([
    state()
], PasActivityView.prototype, "viewedPatientIds", void 0);
__decorate([
    state()
], PasActivityView.prototype, "submitting", void 0);
__decorate([
    state()
], PasActivityView.prototype, "result", void 0);
__decorate([
    state()
], PasActivityView.prototype, "submitError", void 0);
__decorate([
    state()
], PasActivityView.prototype, "lockInfo", void 0);
__decorate([
    state()
], PasActivityView.prototype, "attemptInfo", void 0);
__decorate([
    state()
], PasActivityView.prototype, "supportState", void 0);
__decorate([
    state()
], PasActivityView.prototype, "supportTicket", void 0);
PasActivityView = __decorate([
    customElement("pas-activity-view")
], PasActivityView);
export { PasActivityView };
